import { useOutletContext } from "react-router-dom";
import { Bone, Cpu, Sparkles, Footprints, Plane, ScanLine } from "lucide-react";
import { Container, Reveal, Eyebrow, SectionHeading, PageBanner, siteImg } from "@/site/ui";
import CallbackForm from "@/site/CallbackForm";

const ICONS = [Bone, Cpu, Sparkles, Footprints, Plane, ScanLine];

export default function Services() {
  const c = useOutletContext();
  return (
    <div data-testid="services-page">
      <PageBanner image={c.banners?.services} eyebrow="Our Services" title="Complete prosthetic & orthotic care" subtitle="From advanced bionic limbs to everyday foot care, delivered with precision technology and genuine warmth." />

      {/* Highlights */}
      <section className="pt-16 md:pt-20 pb-16">
        <Container className="grid md:grid-cols-3 gap-6">
          {c.highlights.map((h, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <article className="group h-full rounded-3xl overflow-hidden bg-white border border-gray-100 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-xl" data-testid={`svc-highlight-${i}`}>
                <div className="h-52 overflow-hidden"><img src={siteImg(h.image)} alt={h.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                <div className="p-7">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--rgh-primary)]">{h.subtitle}</p>
                  <h3 className="mt-2 text-xl font-bold tracking-tight">{h.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#525252]">{h.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </Container>
      </section>

      {/* Detailed services */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <Container>
          <Reveal><SectionHeading center eyebrow="Everything we offer" title="Specialised programmes" /></Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.services.map((s, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <Reveal key={i} delay={(i % 3) * 0.08}>
                  <article data-testid={`svc-${i}`} className="group h-full rounded-2xl border border-gray-100 bg-white p-8 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[rgb(var(--rgh-primary-rgb)/0.08)] text-[var(--rgh-primary)] transition-colors duration-300 group-hover:bg-[var(--rgh-primary)] group-hover:text-white"><Icon size={22} /></span>
                    <h3 className="mt-5 text-lg font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-[#525252]">{s.desc}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      <section id="callback" className="py-16 md:py-24 scroll-mt-24">
        <Container>
          <Reveal>
            <div className="rounded-[32px] bg-[#0A0A0A] p-8 sm:p-12 md:p-16">
              <div className="max-w-2xl">
                <Eyebrow>Ready to begin?</Eyebrow>
                <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-white">Request a callback</h2>
                <p className="mt-4 text-lg leading-relaxed text-white/70">Tell us how to reach you and our specialists will help you choose the right care.</p>
              </div>
              <div className="mt-8"><CallbackForm dark /></div>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
